import axios from 'axios';
import { useContext } from 'react';
import { FormValues } from '../../components/Admin/FormSection/interfaces';
import { appUrl } from '../../conf';
import { AxiosContext } from '../../providers/axiosContext';
import {
  AnalyticsParams,
  BenefitsType,
  ImageDTO,
  POI,
  PointTypesResponse,
  PointServicesResponse,
  BenefitsResponse,
} from './dto';
import { ActualFileObject } from 'filepond';
import { useBenefitHistoryStore } from '../benefitsService/benefit.store';
import { CouponDTO } from '../benefitsService/benefit.dto';
import { AuthContext } from '../../providers/authContext';

export interface BikletaPointsService {
  fetchPOIs: (params?: any) => Promise<POI[]>;
  registerPoint: (data: any) => Promise<any>;
  postPhotos: (data: any, id: string) => Promise<any>;
  updatePhotos: (data: any, id: string) => Promise<any>;
  getMyBPs: () => Promise<any>;
  editPointData: (idPoint: string, data: any) => Promise<any>;
  updateService: (pointId: string, serviceData: any) => Promise<any>;
  updateBenefit: (benefitId: string, benefitData: any) => Promise<any>;
  createBenefit: (pointId: string, benefitData: any) => Promise<any>;
  deleteBenefit: (benefitId: string) => Promise<any>;
  getStatistics: (idPoint: string) => Promise<any>;
  getBenefits: (
    idPoint: string,
    params?: { page?: number; limit?: number }
  ) => Promise<BenefitsResponse>;
  postImage: (image: ActualFileObject) => Promise<ImageDTO>;
  checkCoupon: (code: string) => Promise<any>;
  postAnalytics: (data: AnalyticsParams) => Promise<any>;
  getPointTypes: () => Promise<PointTypesResponse>;
  getPointServices: () => Promise<PointServicesResponse>;
}

export const useBikletaPointsService = (): BikletaPointsService => {
  const axiosContext = useContext(AxiosContext);
  const authContext = useContext(AuthContext);

  const apiUrl = process.env.REACT_APP_API_URL_LATEST;
  const axiosInstance = axios.create();

  const fetchPOIs = async search => {
    const res = await axios.get(appUrl + '/web/searchEngine/commerce', {
      params: {
        search,
      },
    });
    return res.data;
  };

  const getMyBPs = async () => {
    try {
      const res = await axiosContext.newAuthAxios.get('/api/points/me');
      return res.data.data;
    } catch (error) {
      console.log(error);
      return error;
    }
  };

  const editPointData = async (pointId, data) => {
    const res = await axiosContext.newAuthAxios.patch(
      `/api/points/${pointId}`,
      data
    );
    return res.data;
  };

  const registerPoint = async data => {
    const res = await axiosContext.newAuthAxios.post('/api/points', data);
    return res.data.data;
  };

  const updateService = async (pointId: string, servicesIds: string[]) => {
    const res = await axiosContext.newAuthAxios.patch(
      `/api/points/${pointId}`,
      {
        services: servicesIds,
      }
    );

    return res.data;
  };

  const updateBenefit = async (benefitId: string, benefitData) => {
    const { data } = await axiosContext.newAuthAxios.patch(
      `/api/points/benefits/${benefitId}`,
      {
        ...benefitData,
      }
    );

    return data.data;
  };

  const createBenefit = async (pointId: string, benefitData) => {
    const res = await axiosContext.newAuthAxios.post('/api/points/benefits', {
      ...benefitData,
      pointId,
    });

    return res.data;
  };

  const deleteBenefit = async (benefitId: string) => {
    const { data } = await axiosContext.newAuthAxios.delete(
      `/api/points/benefits/${benefitId}`
    );

    return data.data;
  };

  const postPhotos = async (data, id) => {
    const res = await axiosContext.authAxios.post(
      '/web/bikleta-points/' + id + '/images',
      data,
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return res.data;
  };

  //TODO: Update point photos ----------------------------------------------------
  const updatePhotos = async (data, id) => {
    const res = await axiosContext.authAxios.put(
      '/web/bikleta-points/' + id + '/images',
      data,
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return res.data;
  };

  const getStatistics = async (id: string) => {
    const res = await axiosContext.authAxios.get(
      '/web/bikleta-points/' + id + '/statistics'
    );
    //console.log(res.data);

    return res.data;
  };

  const postAnalytics = async (data: AnalyticsParams) => {
    const token = authContext.getAccessToken();
    const res = await axiosInstance.post(
      apiUrl + '/api/points/analytics',
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res.data;
  };

  const getBenefits = async (
    id: string,
    params?: { page?: number; limit?: number }
  ) => {
    const { data } = await axiosContext.newAuthAxios.get(
      `/api/points/benefits`,
      {
        params: {
          pointId: id,
          page: params?.page || 1,
          limit: params?.limit || 100,
        },
      }
    );

    return data.data;
  };

  const postImage = async (image: ActualFileObject) => {
    try {
      const formData = new FormData();
      formData.append('image', image);
      formData.append('folder', 'bikleta-points');

      const { data: res } = await axiosContext.newAuthAxios.post(
        '/api/file-management/images',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (res && res.data.secure_url) {
        return {
          url: res.data.secure_url,
        };
      } else if (
        Array.isArray(res.data) &&
        res.data.length > 0 &&
        res.data[0].url
      ) {
        return res.data[0];
      } else {
        console.error('SERVICE - Unexpected response format:', res.data);
        throw new Error('Formato de respuesta inesperado del servidor');
      }
    } catch (error) {
      console.error('SERVICE - Error uploading image:', error);
      throw error;
    }
  };

  const checkCoupon = async (code: string): Promise<CouponDTO> => {
    const res = await axiosContext.authAxios.delete(`/api/v1/coupons/${code}`);
    return {
      ...res.data,
      startValidDate: new Date(res.data.startValidDate),
      expirationDate: new Date(res.data.expirationDate),
    };
  };

  const getPointTypes = async (): Promise<PointTypesResponse> => {
    const res = await axiosContext.newAuthAxios.get(
      '/api/points/types?sortBy=label&order=asc&limit=50'
    );
    return res.data.data;
  };

  const getPointServices = async (): Promise<PointServicesResponse> => {
    const res = await axiosContext.newAuthAxios.get(
      '/api/points/services?sortBy=label&order=asc&limit=50'
    );
    return res.data.data;
  };

  return {
    fetchPOIs,
    registerPoint,
    postPhotos,
    updatePhotos,
    getMyBPs,
    editPointData,
    updateService,
    updateBenefit,
    createBenefit,
    deleteBenefit,
    getStatistics,
    getBenefits,
    postImage,
    checkCoupon,
    postAnalytics,
    getPointTypes,
    getPointServices,
  };
};
